import React from "react";
import { Messages, QueryParamsOf, ResponseOf } from "../../core/api";
import { EntityCardLink } from "../../shared/components/EntityCard";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { createColumnHelper } from "@tanstack/react-table";
import { Center, Text } from "@chakra-ui/react";
import DataTable from "../../shared/components/DataTable/DataTable";
import useQueryDataTable from "../../shared/components/DataTable/useQueryDataTable";
import { PatientTaskInstanceId } from "../../shared/schema/schema";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { Geo } from "../../../../public/admin/scripts/messages/address_component";
interface Props
  extends QueryParamsOf<"get", "./patient_task_instances/:patientTaskInstanceId/metrics"> {
  patientTaskInstanceId: PatientTaskInstanceId;
  queryOptions: UseQueryOptions<
    ResponseOf<"get", "./patient_task_instances/:patientTaskInstanceId/metrics">
  >;
  filterNode: React.ReactNode;
  onClickCaregiver: () => void;
  onClickRow: (addressGeoLocation: Geo, photoUrl: string | null) => void;
}

const columnHelper = createColumnHelper<Messages["PatientTaskInstanceMetricTableData"]>();

const PatientTaskMetricsTable = React.memo(function PatientTaskMetricsTable(props: Props) {
  const columns = [
    columnHelper.accessor("caregiver", {
      cell: (info) => {
        const row = info.row.original;
        return (
          <EntityCardLink
            boxProps={{
              height: "42px",
            }}
            entity={{
              type: "Caregiver",
              id: row.caregiver.id,
              displayId: row.caregiver.displayId,
              fullName: row.caregiver.name,
              photoUrl: row.caregiver.photoUrl,
              status: row.caregiver.status,
            }}
            onClick={props.onClickCaregiver}
          />
        );
      },
      header: () => <span>Caregiver</span>,
    }),
    columnHelper.accessor("createdAt", {
      sortingFn: "instant",
      cell: (info) => (
        <Center>
          <Text>{dateFormatter.toDateTime(info.getValue(), { timezone: "America/New_York" })}</Text>
        </Center>
      ),
      header: () => <span>Created At</span>,
    }),
    columnHelper.accessor("distanceInMiles", {
      sortingFn: "alphanumeric",
      cell: (info) => <Center>{info.getValue()}</Center>,
      header: () => <span>Distance (Miles)</span>,
    }),
    columnHelper.accessor("zipCode", {
      cell: (info) => <Center>{info.getValue()}</Center>,
      header: () => <span>Zip Code</span>,
    }),
  ];

  const { dataTableProps } = useQueryDataTable({
    query: useQuery({ ...props.queryOptions, select: (data) => data.metrics }),
    columns,
    columnVisiblity: {
      storage: { key: "patient-task-metrics", version: 1 },
      forceVisibleColumns: React.useMemo(() => ["caregiver"], []),
      initialSelected: ["caregiver", "createdAt", "distanceInMiles", "zipCode"],
    },
    initialSorting: [
      {
        id: "createdAt",
        desc: true,
      },
    ],
    paginationSize: 10,
  });

  return (
    <DataTable
      {...dataTableProps}
      filterNode={props.filterNode}
      spacing="tight"
      tableSize="sm"
      onClickRow={(e, row) => {
        e.stopPropagation();
        e.preventDefault();
        props.onClickRow(
          row.original.caregiver.addressGeoLocation,
          row.original.caregiver.photoUrl
        );
      }}
    />
  );
});

export default PatientTaskMetricsTable;
